import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { Button, Skeleton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { AutoHealConfig } from '@prisma/client';
import { IconAlertCircle, IconAlertSquareRounded, IconRefresh, IconSparkles } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { erroredOperationAtom, isMultitabTestAtom } from '~/atoms/no-code-atoms';
import { autoHealStepAtom, completedItemsAtom, timeoutAtom } from '~/atoms/recorder-atoms';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { usePreviewFromLocation } from '~/hooks/vm/usePreviewFromLocation';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import { AIGenerationInfoPanel } from './AIGenerationInfoPanel';
import NewOperationControl from './NewOperationControl';
import NoActionsPanel from './NoActionsPanel';
import Operation from './Operation';

export const DEFAULT_EMPTY_PREV_OPERATION_ORDER = -1;
const TIMEOUT_UI_DELAY_MS = 3000;

export default function OperationList({
  step,
  testContentId,
  operations,
  readonly,
  disabled,
  stepIdx,
  handleOperationClick,
  autoHealConfig,
  autoHealCycleLimit,
}: {
  step: SelectedStep;
  testContentId: string;
  operations: SelectedOperation[];
  readonly?: boolean;
  disabled?: boolean;
  stepIdx: number;
  handleOperationClick?: (event: React.MouseEvent<HTMLDivElement>, operationId: string, step: SelectedStep) => void;
  autoHealConfig: AutoHealConfig;
  autoHealCycleLimit: number;
}) {
  const { vmStatus } = useLiveStateManager();

  const hasMultipleTabs = useAtomValue(isMultitabTestAtom);
  const erroredOperation = useAtomValue(erroredOperationAtom);
  const [listAnimationParent] = useAutoAnimate();
  const [mainContainerAnimationParent] = useAutoAnimate();
  const { recorderState } = useLiveStateManager();
  const autoHealStepId = useAtomValue(autoHealStepAtom);
  const completedItems = useAtomValue(completedItemsAtom);
  const timeoutInfo = useAtomValue(timeoutAtom);
  const [currentTimeoutMs, setCurrentTimeoutMs] = useState<number | undefined>(undefined);
  const [showTimeoutUI, setShowTimeoutUI] = useState(false);
  const previewFromLocation = usePreviewFromLocation({ testContentId });

  const trpcUtils = api.useUtils();
  const { mutate: updateStep, isPending: isSaving } = api.testStep.update.useMutation({
    onSuccess: () => {
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.testContent.getCode.invalidate().catch(() => undefined);
      notifications.show({ message: 'Actions saved successfully', color: 'blue' });
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error saving actions', color: 'red' });
    },
  });

  const { mutate: clearStepOperations } = api.operation.clearStepOperations.useMutation({
    onSuccess: (data) => {
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.testContent.getCode.invalidate().catch(() => undefined);
      notifications.show({
        message: `${data.count} generated action${data.count === 1 ? '' : 's'} cleared successfully`,
        color: 'blue',
      });
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error clearing generated actions', color: 'red' });
    },
  });

  // Once receiving a timeout event, we will wait TIMEOUT_UI_DELAY_MS, then show a countdown until auto-heal is triggered
  // This logic is done in the frontend to avoid concurrency logic in the backend while executing the operation
  useEffect(() => {
    if (timeoutInfo?.stepId === step.id && timeoutInfo.totalTimeoutMs) {
      const startTime = Date.now();
      setCurrentTimeoutMs(timeoutInfo.totalTimeoutMs);
      setShowTimeoutUI(false);

      const showUITimeout = setTimeout(() => {
        setShowTimeoutUI(true);
      }, TIMEOUT_UI_DELAY_MS);

      let animationFrameId: number;
      const updateCountdown = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, timeoutInfo.totalTimeoutMs - elapsed);
        setCurrentTimeoutMs(remaining);
        if (remaining <= 0) {
          setShowTimeoutUI(false);
        } else {
          animationFrameId = requestAnimationFrame(updateCountdown);
        }
      };
      animationFrameId = requestAnimationFrame(updateCountdown);
      return () => {
        clearTimeout(showUITimeout);
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    } else {
      setShowTimeoutUI(false);
      setCurrentTimeoutMs(undefined);
    }
  }, [timeoutInfo, step.id]);

  const replayingStatus = useMemo(() => {
    if (vmStatus?.state === 'replaying') {
      const isReplayingOp =
        step.operations.some(
          (op) =>
            op.id === vmStatus?.executionState?.operationId ||
            op.id === vmStatus?.executionState?.stepSourceOperationId,
        ) ||
        // Hack: we hard-code ai step step-ids as operation ids
        vmStatus.executionState?.operationId === step.id;
      if (isReplayingOp) {
        return 'replaying';
      }
      if (completedItems.completedStepIds.includes(step.id)) {
        return 'success';
      }
    } else if (vmStatus?.state === 'error') {
      const isReplayingOp =
        step.operations.some(
          (op) =>
            op.id === vmStatus?.executionState?.operationId ||
            op.id === vmStatus?.executionState?.stepSourceOperationId,
        ) ||
        // Hack: we hard-code ai step step-ids as operation ids
        vmStatus.executionState?.operationId === step.id;
      if (isReplayingOp) {
        return 'fail';
      }
    }
    return undefined;
  }, [
    step.id,
    step.operations,
    completedItems.completedStepIds,
    vmStatus?.executionState?.operationId,
    vmStatus?.executionState?.stepSourceOperationId,
    vmStatus?.state,
  ]);

  const isGenerating =
    replayingStatus === 'replaying' &&
    (((step.mode === 'AI_DYNAMIC' || step.mode === 'AUTH') && vmStatus?.state !== 'finished') ||
      autoHealStepId === step.id);

  return (
    <div
      className={clsx('w-full pr-4 pl-2.5', {
        'pt-4 pb-3': step.mode === 'AI_DYNAMIC' || operations.length === 0,
        'pb-2': step.mode !== 'AI_DYNAMIC' && operations.length > 0,
      })}
      ref={mainContainerAnimationParent}
    >
      {step.mode === 'AI_DYNAMIC' && (
        <div className="px-[44px] pt-2 text-sm font-medium">
          {replayingStatus === 'replaying' && !!recorderState ?
            <AIGenerationInfoPanel />
          : <div>
              {operations.length > 0 ?
                <div className="space-y-3">
                  <div className="rounded-md border border-orange-500 bg-orange-50 px-3 py-2.5 dark:border-orange-400 dark:bg-orange-900/30">
                    <div className="mb-2.5 flex items-center gap-2">
                      <IconAlertCircle
                        size={16}
                        className="flex-shrink-0 text-orange-600 dark:text-orange-400"
                      />
                      <span className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                        Unsuccessful Generation From Previous Run
                      </span>
                    </div>
                    {!readonly && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="compact-xs"
                          variant="filled"
                          color="orange"
                          leftSection={<IconRefresh size={12} />}
                          onClick={() => {
                            previewFromLocation({ step });
                          }}
                        >
                          Regenerate
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="light"
                          loading={isSaving}
                          onClick={() => {
                            updateStep({
                              stepId: step.id,
                              data: { mode: 'AI_FALLBACK' },
                            });
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="default"
                          onClick={() => {
                            clearStepOperations({
                              stepId: step.id,
                            });
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              : <div className="rounded-md bg-yellow-50 px-3 py-1 text-sm text-gray-600 dark:bg-yellow-800/20 dark:text-gray-400">
                  Step is un-generated. Preview to generate actions
                </div>
              }
            </div>
          }

          {/* Deprecation Notice for Read-Only View */}
          {!!readonly &&
            !(
              vmStatus?.state === 'loading' ||
              vmStatus?.state === 'replaying' ||
              recorderState?.executionType === 'preview'
            ) && (
              <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                <div className="flex items-start space-x-2">
                  <IconAlertSquareRounded
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-orange-600 dark:text-orange-400"
                  />
                  <div className="text-xs">
                    <div className="mb-1 font-medium text-orange-800 dark:text-orange-200">Deprecated</div>
                    <div className="text-orange-700 dark:text-orange-300">
                      If this step is truly dynamic, migrate to an <strong>&ldquo;AI Act&rdquo;</strong> action. Search
                      for &ldquo;AI Act&rdquo; in the editor.
                      <br />
                      <br />
                      If the content is not dynamic, migrate to <strong>&ldquo;Auto-heal&rdquo;</strong> step mode by
                      clicking the <strong>Save</strong> button for this step when editing the test.
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}
      {step.mode === 'AUTH' && (
        <div className="px-7 pt-4 text-sm font-medium text-gray-500">
          {step.operations.length === 0 ?
            <div>
              {replayingStatus === 'replaying' && !!recorderState ?
                <AIGenerationInfoPanel />
              : <div className="flex items-center gap-2 pb-2">
                  <div>AI will generate actions for this step.</div>
                </div>
              }
            </div>
          : <div />}
        </div>
      )}
      {!(step.mode === 'AI_DYNAMIC' || step.mode === 'AUTH' || autoHealStepId === step.id) &&
        operations.length === 0 && (
          <div className="px-[44px] pt-2">
            <NoActionsPanel
              testContentId={testContentId}
              step={step}
              executing={recorderState !== undefined}
              readOnly={!!readonly}
            />
          </div>
        )}
      <ul
        ref={listAnimationParent}
        className={clsx({
          'py-0': operations.length === 0 && recorderState?.stepId !== step.id,
          'py-3': operations.length > 0 || recorderState?.stepId === step.id,
        })}
      >
        {autoHealStepId === step.id && replayingStatus === 'replaying' && (
          <div
            className={clsx('px-[44px] text-sm font-medium text-gray-500', {
              'pt-0': operations.length === 0,
              'pt-5': operations.length > 0,
            })}
          >
            <div className="flex items-center gap-1">
              <IconAlertCircle size={16} /> <span>Previous actions failed</span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs">
              <IconSparkles
                className="flex-none"
                size={16}
              />
              <span>
                AI is auto-healing{' '}
                {match(autoHealConfig)
                  .with('LOCATOR_ONLY', () => 'broken locators')
                  .with('UI', () => `minor UI changes with up to ${autoHealCycleLimit} exploration cycles`)
                  .with('USER_FLOW', () => `user flow changes with up to ${autoHealCycleLimit} exploration cycles`)
                  .exhaustive()}
              </span>
            </div>
          </div>
        )}
        {operations.map((operation, index) => {
          return (
            <li
              key={operation.id}
              // We add another animationParent here so that we get a scroll when adding new operations
              ref={listAnimationParent}
            >
              <NewOperationControl
                readonly={readonly}
                testContentId={testContentId}
                prevOperationId={index > 0 ? operations[index - 1].id : undefined}
                nextOperationId={operation.id}
                stepId={step.id}
                stepOrder={step.stepOrder}
                stepMode={step.mode}
                insertAfterOperationOrder={index > 0 ? operations[index - 1].order : DEFAULT_EMPTY_PREV_OPERATION_ORDER}
                operationId={index > 0 ? operations[index - 1].id : undefined}
                activePageAlias={
                  index > 0 ?
                    operations[index - 1].opensPageAlias || operations[index - 1].pageAlias
                  : operations[0].pageAlias
                }
              />
              <div
                className="w-full"
                onClick={(e) => handleOperationClick?.(e, operation.id, step)}
              >
                <Operation
                  className={clsx('w-full', {
                    'rounded-md border-2 border-red-500 p-1 dark:border-red-400':
                      !!erroredOperation && erroredOperation?.id === operation.id,
                    'opacity-60': operation.skip,
                  })}
                  operation={operation}
                  testContentId={testContentId}
                  step={step}
                  showTabLabel={
                    // Show tab label for all operations when there are multiple tabs
                    // (excluding DEFINE_VARIABLE, RECEIVE_EMAIL, WAIT which don't have page context)
                    !!hasMultipleTabs &&
                    operation.type !== 'DEFINE_VARIABLE' &&
                    operation.type !== 'RECEIVE_EMAIL' &&
                    operation.type !== 'WAIT'
                  }
                  readonly={readonly}
                  disabled={disabled}
                  stepIdx={stepIdx}
                  timeoutInfo={
                    (
                      showTimeoutUI &&
                      currentTimeoutMs !== undefined &&
                      currentTimeoutMs >= 0 &&
                      vmStatus?.executionState?.operationId === operation.id
                    ) ?
                      { currentTimeoutMs, totalTimeoutMs: timeoutInfo?.totalTimeoutMs }
                    : undefined
                  }
                />
              </div>
            </li>
          );
        })}
        {isGenerating ?
          <div className="px-10 pt-3">
            <Skeleton className="h-8" />
          </div>
        : <li key={`new-operation-${operations.at(-1)?.id ?? ''}`}>
            <NewOperationControl
              testContentId={testContentId}
              readonly={!!readonly && recorderState?.executionType !== 'record'}
              prevOperationId={operations.at(-1)?.id}
              stepId={step.id}
              stepOrder={step.stepOrder}
              stepMode={step.mode}
              insertAfterOperationOrder={operations.at(-1)?.order ?? DEFAULT_EMPTY_PREV_OPERATION_ORDER}
              operationId={operations.at(-1)?.id}
              activePageAlias={operations.at(-1)?.opensPageAlias || operations.at(-1)?.pageAlias}
            />
          </li>
        }
      </ul>
    </div>
  );
}
